import React from 'react';
import styled from 'styled-components';
import { FaGithub, FaExternalLinkAlt, FaStar } from 'react-icons/fa';

const ProjectsSection = styled.section`
  padding: 70px;
  color: #fff;
  max-width: 800px;
  margin: 20px auto 0 auto;
  font-family: "Garamond", serif;
  background-color: rgb(23, 29, 41);

  @media (max-width: 768px) {
    padding: 50px 20px;
  }

  @media (max-width: 480px) {
    padding: 30px 15px;
  }
`;

const ProjectList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const ProjectItem = styled.li`
  background-color: rgb(46, 57, 80);
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  transition: background-color 0.3s;
  display: flex;
  justify-content: space-between;
  gap: 20px;

  &:hover {
    background-color: #444;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProjectInfo = styled.div`
  flex: 1;

  li {
    margin-bottom: 10px;
    line-height: 1.6;
    font-size: 1.15em;

    @media (max-width: 480px) {
      font-size: 0.95em;
    }
  }

  br {
    line-height: 2;
  }
`;

const ProjectTitle = styled.h2`
  font-size: 1.3em;
  margin-bottom: 12px;

  @media (max-width: 480px) {
    font-size: 1.1em;
  }
`;

const ProjectLink = styled.a`
  color: #1E90FF;
  text-decoration: none;
  font-size: 1.5em;
  min-width: 40px;
  margin-top: 10px;
  display: flex;
  align-self: flex-start;

  @media (max-width: 768px) {
    align-self: flex-end;
  }

  &:hover {
    color: #63b3ed;
  }
`;

const HighlightedPoint = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  font-size: 1.05em;
  color: #ffc107;

  svg {
    margin-right: 6px;
  }

  @media (max-width: 480px) {
    font-size: 1em;
  }
`;

const Projects = () => {
  return (
    <ProjectsSection id="projects">
      <h1>Projects</h1>
      <ProjectList>

        <ProjectItem>
          <ProjectInfo>
            <ProjectTitle>Beyond School</ProjectTitle>
            <ul>
              <li>Beyond School is a site for kids to learn beyond their curriculum and gain knowledge on important life skills like Mental health, financial literacy, disaster preparedness, sex education, environment awareness, cooking and home management.</li>
              <li>It is also integrated with a chatbot using the Gemini API to answer any queries instantly.</li>
            </ul>
          </ProjectInfo>
          <ProjectLink
            href="https://github.com/Amirtha2503/BeyondSchool"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGithub />
          </ProjectLink>
        </ProjectItem>

        <ProjectItem>
          <ProjectInfo>
            <ProjectTitle>Enhancing Forecasting Precision in Indian Industrial Workers’ Inflation</ProjectTitle>
            <ul>
              <li>Performed Time series analysis and compared the performance of Long Short-Term Memory (LSTM), bidirectional LSTM (BiLSTM), XGBoost, and Random Forest models for predicting future inflation values in Industrial Worker's inflation data.</li>
              <li>Concluded that the BiLSTM model performs the best in terms of Mean Squared Error(MSE) and accuracy in forecast values.</li>
            </ul>
            <HighlightedPoint>
              <FaStar />
              Presented as a research paper at the 2024 International Conference on Wireless Communications Signal Processing and Networking (WISPNET)
            </HighlightedPoint>
          </ProjectInfo>
              <ProjectLink
            href="https://ieeexplore.ieee.org/document/10532954"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaExternalLinkAlt />
            </ProjectLink>
        </ProjectItem>

        <ProjectItem>
          <ProjectInfo>
            <ProjectTitle>Impact of Deep Learning in Grammar Correction</ProjectTitle>
            <ul>
              <li>Analyzed and compared various grammar error correction models, concluding that the transformer model performed best with a GLEU score of 0.855.</li>
            </ul>
            <HighlightedPoint>
              <FaStar />
              Presented as a research paper at the 2023 Fourth International Conference on Smart Technologies in Computing Electrical and Electronics(ICSTCEE).
            </HighlightedPoint>
          </ProjectInfo>
          <ProjectLink
            //href="https://github.com/Amirtha2503/GrammarErrorCorrection"
            href="https://ieeexplore.ieee.org/abstract/document/10584963"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaExternalLinkAlt />
          </ProjectLink>
        </ProjectItem>

        <ProjectItem>
          <ProjectInfo>
            <ProjectTitle>DevCraftsman</ProjectTitle>
            <ul>
              <li>DevCraftsman is a tool designed to help code developers improve the quality, performance, and maintainability of their software.</li>
              <li>Collaborated with a cross-functional team to build a tool assisting developers with bug identification, data visualization, and code understanding.</li>
            </ul>
            <HighlightedPoint>
              <FaStar />
              Won the DeveloperWeek 2023 hackathon in the Efficient Microservices Applications category sponsored by Oracle.
            </HighlightedPoint>
          </ProjectInfo>
          <ProjectLink
            href="https://github.com/Amirtha2503/Developer-week_Front-end"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGithub />
          </ProjectLink>
        </ProjectItem>

        <ProjectItem>
          <ProjectInfo>
            <ProjectTitle>Analysis of the EHRKit Library</ProjectTitle>
            <ul>
              <li>Analyzed and implemented tasks in the EHRKit library, including developing an extractive summarization feature for patient health records from the MIMIC III database.</li>
            </ul>
          </ProjectInfo>
          <ProjectLink
            href="https://github.com/Amirtha2503/EHRKIT-Buddi-AI"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGithub />
          </ProjectLink>
        </ProjectItem>

        <ProjectItem>
          <ProjectInfo>
            <ProjectTitle>Head Gesture Controlled Wheelchair</ProjectTitle>
            <ul>
              <li>Utilized Arduino microcontroller and IoT sensors to create a prototype for a head movement-controlled wheelchair for quadriplegic patients.</li>
            </ul>
          </ProjectInfo>
          <ProjectLink
            href="https://www.tinkercad.com/things/0o7aC282Lbc-head-gesture-controlled-wheelchair"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaExternalLinkAlt />
          </ProjectLink>
        </ProjectItem>

      </ProjectList>
    </ProjectsSection>
  );
};

export default Projects;
